import _ from 'lodash';
import {
  reactive,
  Ref,
  ref,
  UnwrapRef,
  getCurrentInstance,
  ComponentInternalInstance,
  computed,
} from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import openPreviewHandler from '@/renderer/components/SpacePage/compositions/openPreviewHandler';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';
import { isAllowPreviewImage, isAllowPrivate, isAllowEditDoc } from '@/renderer/scripts/composition/allowPreview';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';

type Handler = {
  curHistoryId: Ref<string>;
  loading: Ref<boolean>;
  list: Ref<SMH.HistoryItem[]>;
  historyConfig: UnwrapRef<SMH.GetLibraryHistoryExtension>;
  methods: {
    pullHistoryList: (item: any) => void;
    setLatestVersion: (item: any) => void;
    deleteHistory: (item: Array<any>) => void;
    getHistoryConfig: () => void;
  };
};
export default function handler(spaceId?: Ref<SpaceId>): Handler {
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const curHistoryId = computed(() => vm.$route.query.historyId as string);
  const loading = ref(false);
  const list = ref<SMH.HistoryItem[]>([]);
  const historyConfig = reactive<SMH.GetLibraryHistoryExtension>({
    enableFileHistory: true,
    fileHistoryCount: 1000,
    fileHistoryExpireDay: 30,
  });
  let pathKeep: Array<string>;
  const { methods: previewMethods } = openPreviewHandler(spaceId);
  const methods = {
    async pullHistoryList(item: any) {
      pathKeep = item.path;
      const opt = {
        page: 1,
        // 参数可不传pageSize, 后台有多少返多少
        orderBy: 'creationTime',
        orderByType: 'desc',
        dirPath: item.path.join('/'),
        spaceId: spaceId?.value,
        spaceOrgId: item.spaceOrgId || vm.$store.getters.orgId,
      };
      loading.value = true;
      try {
        const res = await smh.userAPI.getHistoryList(opt);
        list.value = res.data.contents as SMH.HistoryItem[];
        list.value.forEach((i: any) => {
          i.check = ref(false);
          i.path = pathKeep;
          i.contentType = item.contentType;
          i.historyId = i.id;
          i.previewByDoc = item.previewByDoc;
          i.previewByCI = item.previewByCI;
          i.authorityList = item.authorityList;
        });
        list.value = _.sortBy(list.value, (item: any) => (item.isLatestVersion ? 0 : 1));
      } catch (error) {
        tips.error(error as MsgError);
      }
      loading.value = false;
    },
    async setLatestVersion(item: any) {
      const opt = {
        historyId: item.id,
        spaceId: spaceId?.value,
      };
      const deleteDialog = CustomDialog.confirm({
        header: i18n_t('提示'),
        body: i18n_t('确认将文件恢复到该版本吗'),
        theme: 'warning',
        onConfirm: async () => {
          if (deleteDialog.hide) {
            deleteDialog.hide();
          }
          try {
            await smh.commonAPI.setLatestVersion(opt as SMH.SetLatestVersionParams);
            tips.success(i18n_t('设置成功'));
            item.path = pathKeep;
            await methods.pullHistoryList(item);
            if (vm.$route.path.includes('/preview/')) {
              const cannotPreviewImg = isAllowPreviewImage(item.name) && item.size >= ConstantConfig.ciImageMaxSize; // 万象最大支持32mb图片预览
              const { isPrivate } = vm.$store.getters;
              const cannotPreviewDoc = !vm.$store.getters.enableDocPreview; // 不可预览文档
              const cannotPreviewOthers = !isAllowPrivate(item.name); // 不可预览音视频、pdf
              const cannotPreviewInPrivate = isPrivate && cannotPreviewDoc && cannotPreviewOthers;
              if (cannotPreviewImg || cannotPreviewInPrivate) {
                return;
              }
              previewMethods.openPreview(item, 1);
            }
          } catch (error) {
            tips.error(error as MsgError);
          }
        },
      });
    },
    async deleteHistory(item: Array<any>) {
      const opt = {
        historyIds:
          item.length > 1
            ? item.reduce((arr, i) => {
                i.check.value && arr.push(i.id);
                return arr;
              }, [])
            : [item[0].id],
        spaceId: spaceId?.value,
      };
      const deleteDialog = CustomDialog.confirm({
        header:
          item.length > 1
            ? i18n_t('确认要删除已选的{slot1}个版本吗？', {
                slot1: item.filter((i: any) => i.check.value).length,
              })
            : i18n_t('确认要删除该版本吗？'),
        body: i18n_t('删除后文件将无法找回'),
        theme: 'warning',
        onConfirm: async () => {
          if (deleteDialog.hide) {
            deleteDialog.hide();
          }
          try {
            await smh.commonAPI.deleteHistory(
              {
                spaceId: spaceId?.value,
              },
              {
                body: opt.historyIds,
              },
            );
            tips.success(i18n_t('删除成功'));
            item[0].path = pathKeep;
            methods.pullHistoryList(item[0]);
          } catch (error) {
            tips.error(error as MsgError);
          }
        },
      });
    },
    async getHistoryConfig() {
      if (vm.$store.getters.editionFlag === 'personal') {
        return;
      }
      try {
        const res = await smh.userAPI.getLibraryHistoryExtension();
        historyConfig.enableFileHistory = res.data.enableFileHistory;
        historyConfig.fileHistoryCount = res.data.fileHistoryCount;
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
  };
  // FIX: 不需要再每次引入时都获取一边历史配置
  // methods.getHistoryConfig();

  return {
    curHistoryId,
    loading,
    list,
    historyConfig,
    methods,
  };
}
